/** The desk's own posts: writing one, editing one, and the panel a manual
 *  channel's post is put up by hand from. Ported from the old Promo posts page
 *  (routes/RedeemPosts.tsx) with its rules unchanged — the server still checks
 *  every one of them. */

import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import {
  BLUESKY_MAX_GRAPHEMES,
  isManualPlatform,
  MANUAL_POST_PLATFORMS,
  type RedeemApp,
  type RedeemCodeMode,
  type RedeemPost,
  type RedeemPostPlatform,
  type RedeemPublicCode,
} from '@/api/client'
import { countGraphemes } from '@/lib/graphemes'
import { usePostActions } from '@/lib/postActions'
import { postUrlError } from '@/lib/postUrl'
import { usePublicCodes, useRedeemApps } from '@/lib/queries'
import { PLATFORM_LABEL, appLabel } from '@/lib/redeem'
import { cn } from '@/lib/utils'
import {
  Button,
  buttonClass,
  Code,
  ConfirmDialog,
  CopyButton,
  ErrorState,
  Field,
  FieldGroup,
  Input,
  MediaPicker,
  Select,
  Textarea,
  errorText,
  useToast,
} from '@/ui'

/** Shown as placeholders only — the server fills these in when left empty. */
export const DEFAULT_COMPOSE: Record<string, { url: string; mode: RedeemCodeMode }> = {
  ppomppu: { url: 'https://www.ppomppu.co.kr/zboard/zboard.php?id=ppomapp', mode: 'body' },
  okky: { url: 'https://okky.kr/community/request-for-comments', mode: 'body' },
  x: { url: 'https://x.com/compose/post', mode: 'reply' },
  threads: { url: 'https://www.threads.net/', mode: 'reply' },
  instagram: { url: 'https://www.instagram.com/', mode: 'reply' },
  tiktok: { url: 'https://www.tiktok.com/upload', mode: 'reply' },
}

export const CODE_MODE_LABEL: Record<RedeemCodeMode, string> = {
  none: 'none — 코드 없음',
  body: 'body — 본문에 번호 목록',
  reply: 'reply — 답글로 코드',
}

const CODES_HINT = '{{codes}} 자리에 번호 목록(1. 코드 - 링크)이 들어간다. 없으면 끝에 붙는다.'

const BLUESKY_BODY_HINT =
  '이 글 전체가 그대로 게시된다(최대 300자). 제목은 게시되지 않고 이미지 대체텍스트로만 쓰인다. 코드는 DM으로 — 본문에 넣지 않는다.'

/** Bluesky counts its 300 in graphemes; the counter says so as you type. */
export function GraphemeCounter({ text }: { text: string }) {
  const n = countGraphemes(text)
  const over = n > BLUESKY_MAX_GRAPHEMES
  return (
    <span className={cn('tabular-nums', over ? 'font-medium text-status-failed' : 'text-muted-foreground')}>
      {n} / {BLUESKY_MAX_GRAPHEMES}
      {over && ' · 너무 길다'}
    </span>
  )
}

/** A web app's shared code: the checkout's discount code, the same for
 *  everyone who asks under this post, DMed one person at a time so the ledger
 *  counts who it reached. One per post, so channels can be told apart. */
export function ShareCodeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field
      label="DM 코드"
      hint="이 글에 요청한 사람에게 DM으로 보낼 공유 코드. 채널마다 다르게 쓰고, 앱 화면에서 사용 수를 먼저 추가한다."
    >
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder="예: SIDEPROJECT20"
        className="font-mono"
      />
    </Field>
  )
}

/** A reddit post's link flair, by its text as the subreddit shows it. The
 *  publisher looks it up before posting and stops if the subreddit has no
 *  such flair, so a typo fails loudly instead of posting bare. */
export function FlairField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="플레어 (선택)" hint="서브레딧에 보이는 이름 그대로. 플레어가 필수인 서브는 이게 없으면 글을 거부한다.">
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="예: Ride Along Story" />
    </Field>
  )
}

function ClaimPhraseField({ value, onChange, bluesky }: { value: string; onChange: (v: string) => void; bluesky: boolean }) {
  return (
    <Field
      label="댓글 문구"
      hint={`이 문구가 들어간 ${bluesky ? 'DM' : '댓글'}만 코드를 받는다. 비워 두면 이 글에는 코드를 보내지 않는다. 본문에 문구를 적어 안내해야 저장된다. 대소문자·문장부호는 무시한다.`}
    >
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="예: shutter up" />
    </Field>
  )
}

function HeroField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <Field label="히어로 이미지">
      <MediaPicker value={value} onChange={onChange} label="히어로 이미지" />
    </Field>
  )
}

/** The fields only a hand-posted channel has: a free-text note on where
 *  exactly, the page to write it on, and where its codes go. */
export function ManualFields({
  platform,
  channel,
  setChannel,
  composeUrl,
  setComposeUrl,
  codeMode,
  setCodeMode,
}: {
  platform: RedeemPostPlatform
  channel: string
  setChannel: (v: string) => void
  composeUrl: string
  setComposeUrl: (v: string) => void
  codeMode: RedeemCodeMode
  setCodeMode: (v: RedeemCodeMode) => void
}) {
  return (
    <>
      <Field label="채널 메모 (선택)">
        <Input
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          placeholder={platform === 'ppomppu' ? '앱정보 · 내가 만든 앱' : platform === 'okky' ? '피드백' : ''}
        />
      </Field>
      <Field label="코드 방식">
        <Select value={codeMode} onChange={(e) => setCodeMode(e.target.value as RedeemCodeMode)}>
          {(['body', 'reply', 'none'] as const).map((m) => (
            <option key={m} value={m}>
              {CODE_MODE_LABEL[m]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="작성 페이지 (비우면 기본값)" className="sm:col-span-2">
        <Input
          value={composeUrl}
          onChange={(e) => setComposeUrl(e.target.value)}
          placeholder={DEFAULT_COMPOSE[platform]?.url}
        />
      </Field>
    </>
  )
}

function bodyHint(manual: boolean, codeMode: RedeemCodeMode, web: boolean, bluesky: boolean): string {
  if (manual) {
    return codeMode === 'body'
      ? `${CODES_HINT} 코드는 직접 쓰지 말고 발급해서 넣는다.`
      : '이 글에는 코드를 넣지 않는다(코드는 답글로).'
  }
  if (web) return '코드는 본문에 쓰지 않는다. 댓글 문구를 단 사람에게 아래 DM 코드가 간다.'
  if (bluesky) return BLUESKY_BODY_HINT
  return '코드 문자열은 넣지 않는다 — 공개되면 선착순이 되고, 서버가 거부한다.'
}

// ── writing one ────────────────────────────────────────────────────────────

/** "데스크 글 쓰기": saved as a draft, scheduled from the schedule afterwards. */
export function DeskPostForm({ onDone, initialAppId }: { onDone: (post?: RedeemPost) => void; initialAppId?: number }) {
  const apps: RedeemApp[] = (useRedeemApps().data?.items ?? []).map((i) => i.app)
  const actions = usePostActions()
  const toast = useToast()
  const [appId, setAppId] = useState(initialAppId ?? 0)
  const [platform, setPlatform] = useState<RedeemPostPlatform>('reddit')
  const [channel, setChannel] = useState('')
  const [composeUrl, setComposeUrl] = useState('')
  const [codeMode, setCodeMode] = useState<RedeemCodeMode>('body')
  const [replyText, setReplyText] = useState('')
  const [claimPhrase, setClaimPhrase] = useState('')
  const [shareCode, setShareCode] = useState('')
  const [flair, setFlair] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [mediaId, setMediaId] = useState(0)
  const [err, setErr] = useState<unknown>(null)

  useEffect(() => {
    if (!appId && apps.length > 0) setAppId(apps[0].id)
  }, [apps.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const manual = isManualPlatform(platform)
  const bluesky = platform === 'bluesky'
  // A web app's post names the shared code its askers are DMed.
  const web = apps.find((a) => a.id === appId)?.platform === 'web'
  const tooLong = bluesky && countGraphemes(body) > BLUESKY_MAX_GRAPHEMES
  const pickPlatform = (p: RedeemPostPlatform) => {
    setPlatform(p)
    setComposeUrl('')
    setCodeMode(DEFAULT_COMPOSE[p]?.mode ?? 'none')
  }

  const submit = async () => {
    setErr(null)
    try {
      const post = await actions.createRedeem.mutateAsync(
        manual
          ? {
              app_id: appId,
              platform,
              channel,
              title,
              body,
              compose_url: composeUrl,
              code_mode: codeMode,
              reply_text: replyText,
            }
          : bluesky
            ? {
                app_id: appId,
                platform: 'bluesky',
                title,
                body,
                media_id: mediaId,
                claim_phrase: claimPhrase,
                share_code: web ? shareCode : '',
              }
            : {
                app_id: appId,
                platform: 'reddit',
                channel,
                title,
                body,
                media_id: mediaId,
                claim_phrase: claimPhrase,
                share_code: web ? shareCode : '',
                flair,
              },
      )
      toast.success('초안으로 저장했다')
      onDone(post)
    } catch (e) {
      setErr(e)
    }
  }

  const ready =
    !!appId && !(platform === 'reddit' && !channel.trim()) && !!title.trim() && !!body.trim() && !tooLong

  return (
    <div className="space-y-4">
      <FieldGroup columns={2}>
        <Field label="앱" required>
          <Select value={String(appId)} onChange={(e) => setAppId(Number(e.target.value))}>
            {apps.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {appLabel(a, apps)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="채널" required>
          <Select value={platform} onChange={(e) => pickPlatform(e.target.value as RedeemPostPlatform)}>
            <option value="reddit">Reddit — 데스크가 게시·감시</option>
            <option value="bluesky">Bluesky — 데스크가 게시·DM 감시</option>
            {MANUAL_POST_PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABEL[p]} — 직접 게시
              </option>
            ))}
          </Select>
        </Field>
        {!manual && !bluesky && (
          <Field label="서브레딧" required>
            <Input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="AppGiveaway" />
          </Field>
        )}
        {!manual && <HeroField value={mediaId} onChange={setMediaId} />}
        <Field
          label={bluesky ? '제목 — 게시되지 않음, 이미지 대체텍스트' : '제목'}
          required
          className="sm:col-span-2"
        >
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        {manual && (
          <ManualFields
            platform={platform}
            channel={channel}
            setChannel={setChannel}
            composeUrl={composeUrl}
            setComposeUrl={setComposeUrl}
            codeMode={codeMode}
            setCodeMode={setCodeMode}
          />
        )}
      </FieldGroup>
      <Field
        label="본문"
        required
        hint={bodyHint(manual, codeMode, web, bluesky)}
        aside={bluesky ? <GraphemeCounter text={body} /> : undefined}
      >
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          placeholder={
            manual && codeMode === 'body'
              ? '선착순 코드입니다.\n\n{{codes}}\n\n쓰신 분은 "N번 사용" 댓글 부탁드립니다.'
              : undefined
          }
        />
      </Field>
      <FieldGroup columns={2}>
        {!manual && web && <ShareCodeField value={shareCode} onChange={setShareCode} />}
        {platform === 'reddit' && <FlairField value={flair} onChange={setFlair} />}
        {!manual && <ClaimPhraseField value={claimPhrase} onChange={setClaimPhrase} bluesky={bluesky} />}
      </FieldGroup>
      {manual && codeMode === 'reply' && (
        <Field label="답글" hint={CODES_HINT}>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={4}
            placeholder={'Codes, first come — reply with the number you used:\n{{codes}}'}
          />
        </Field>
      )}
      {err != null && <ErrorState title="저장하지 못했다" error={err} />}
      <div className="flex justify-end gap-2">
        <Button onClick={() => onDone()}>취소</Button>
        <Button variant="primary" loading={actions.createRedeem.isPending} disabled={!ready} onClick={() => void submit()}>
          초안으로 저장
        </Button>
      </div>
    </div>
  )
}

// ── editing one ────────────────────────────────────────────────────────────

/** The edit tab of a desk post's detail. The platform is fixed at creation;
 *  everything the post says can change until it is published. */
export function DeskPostEditor({
  post,
  app,
  onSaved,
  onCancel,
}: {
  post: RedeemPost
  app?: RedeemApp
  onSaved: () => void
  onCancel: () => void
}) {
  const manual = isManualPlatform(post.platform)
  const bluesky = post.platform === 'bluesky'
  const web = app?.platform === 'web'
  const actions = usePostActions()
  const toast = useToast()
  const [title, setTitle] = useState(post.title)
  const [body, setBody] = useState(post.body)
  const [channel, setChannel] = useState(post.channel)
  const [composeUrl, setComposeUrl] = useState(post.compose_url)
  const [codeMode, setCodeMode] = useState<RedeemCodeMode>(post.code_mode)
  const [replyText, setReplyText] = useState(post.reply_text)
  const [shareCode, setShareCode] = useState(post.share_code ?? '')
  const [flair, setFlair] = useState(post.flair ?? '')
  const [claimPhrase, setClaimPhrase] = useState(post.claim_phrase ?? '')
  const [mediaId, setMediaId] = useState(post.media_id)
  const [err, setErr] = useState<unknown>(null)
  const tooLong = bluesky && countGraphemes(body) > BLUESKY_MAX_GRAPHEMES

  const save = async () => {
    setErr(null)
    try {
      await actions.updateRedeem.mutateAsync({
        id: post.id,
        post: manual
          ? { ...post, title, body, channel, compose_url: composeUrl, code_mode: codeMode, reply_text: replyText }
          : bluesky
            ? { ...post, title, body, share_code: shareCode, media_id: mediaId, claim_phrase: claimPhrase }
            : { ...post, title, body, channel, share_code: shareCode, flair, media_id: mediaId, claim_phrase: claimPhrase },
      })
      toast.success('저장했다')
      onSaved()
    } catch (e) {
      setErr(e)
    }
  }

  return (
    <div className="space-y-4">
      <FieldGroup columns={2}>
        <Field label={bluesky ? '제목 — 이미지 대체텍스트' : '제목'} className="sm:col-span-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        {post.platform === 'reddit' && (
          <Field label="서브레딧">
            <Input value={channel} onChange={(e) => setChannel(e.target.value)} />
          </Field>
        )}
        {!manual && <HeroField value={mediaId} onChange={setMediaId} />}
        {manual && (
          <ManualFields
            platform={post.platform}
            channel={channel}
            setChannel={setChannel}
            composeUrl={composeUrl}
            setComposeUrl={setComposeUrl}
            codeMode={codeMode}
            setCodeMode={setCodeMode}
          />
        )}
      </FieldGroup>
      <Field
        label="본문"
        hint={manual ? CODES_HINT : bluesky ? BLUESKY_BODY_HINT : undefined}
        aside={bluesky ? <GraphemeCounter text={body} /> : undefined}
      >
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
      </Field>
      {manual && codeMode === 'reply' && (
        <Field label="답글" hint={CODES_HINT}>
          <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4} />
        </Field>
      )}
      {!manual && (
        <FieldGroup columns={2}>
          {post.platform === 'reddit' && <FlairField value={flair} onChange={setFlair} />}
          <ClaimPhraseField value={claimPhrase} onChange={setClaimPhrase} bluesky={bluesky} />
          {web && <ShareCodeField value={shareCode} onChange={setShareCode} />}
        </FieldGroup>
      )}
      {err != null && <ErrorState title="저장하지 못했다" error={err} />}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>취소</Button>
        <Button variant="primary" loading={actions.updateRedeem.isPending} disabled={tooLong} onClick={() => void save()}>
          저장
        </Button>
      </div>
    </div>
  )
}

// ── putting a manual post up by hand ───────────────────────────────────────

/** What the operator needs to put a manual post up by hand: the page to write
 *  on, the text to paste with the codes already in it, the codes themselves,
 *  and a place to record where it went. */
export function ManualPanel({ post }: { post: RedeemPost }) {
  const codesQ = usePublicCodes(post.id)
  const codes: RedeemPublicCode[] = codesQ.data?.items ?? []
  const actions = usePostActions()
  const toast = useToast()
  const [count, setCount] = useState(post.public_codes || 10)
  const [url, setUrl] = useState(post.external_url)
  const [askTopUp, setAskTopUp] = useState(false)
  const [toVoid, setToVoid] = useState<RedeemPublicCode | null>(null)

  useEffect(() => setUrl(post.external_url), [post.external_url])

  const live = post.public_codes ?? 0
  const withCodes = post.code_mode !== 'none'
  const more = count - live

  const urlProblem = postUrlError(url)
  const markPublished = async () => {
    try {
      await actions.markRedeemPublished.mutateAsync({ id: post.id, url: url.trim() })
      toast.success(post.status === 'published' ? '주소를 고쳤다' : '게시됨으로 표시했다')
    } catch (e) {
      toast.error(errorText(e))
    }
  }

  return (
    <section aria-label="직접 올리기" className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {post.compose_url && (
          <a
            href={post.compose_url}
            target="_blank"
            rel="noreferrer"
            className={buttonClass('primary', 'sm')}
            title={post.compose_url}
          >
            <ExternalLink size={14} aria-hidden />
            작성 페이지 열기
          </a>
        )}
        <CopyButton text={post.title} label="제목 복사" what="제목" />
        <CopyButton text={post.rendered_body ?? post.body} label="본문 복사" what="본문" />
        {post.code_mode === 'reply' && post.rendered_reply && (
          <CopyButton text={post.rendered_reply} label="답글 복사" what="답글" />
        )}
        <span className="text-meta text-muted-foreground">코드 방식: {CODE_MODE_LABEL[post.code_mode]}</span>
      </div>

      {withCodes && live === 0 && (
        <p className="text-meta font-medium text-status-publishing">
          아직 공개 코드가 없다 — 복사한 {post.code_mode === 'body' ? '본문' : '답글'}에 코드가 빠져 있다.
        </p>
      )}

      {post.code_mode === 'reply' && post.rendered_reply && (
        <details className="text-meta">
          <summary className="cursor-pointer text-muted-foreground">답글 미리보기</summary>
          <pre className="mt-1 whitespace-pre-wrap font-sans text-table text-foreground">{post.rendered_reply}</pre>
        </details>
      )}
      {post.code_mode === 'body' && live > 0 && (
        <details className="text-meta">
          <summary className="cursor-pointer text-muted-foreground">코드가 들어간 본문 미리보기</summary>
          <pre className="mt-1 whitespace-pre-wrap font-sans text-table text-foreground">{post.rendered_body}</pre>
        </details>
      )}

      {withCodes ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-table">
            <span className="text-muted-foreground">
              공개 코드 <span className="tabular-nums">{live}</span>장 보유
            </span>
            <Input
              type="number"
              min={1}
              max={50}
              aria-label="공개 코드 수"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="h-8 w-20 text-table"
            />
            <Button
              size="sm"
              variant="primary"
              disabled={count < 1}
              loading={actions.topUpCodes.isPending}
              title="모자란 만큼만 발급한다. 같은 수로 다시 눌러도 새로 나가지 않는다."
              onClick={() => {
                if (more <= 0) {
                  toast.info(`이미 ${live}장 있다 — 새로 발급할 것은 없다`)
                  return
                }
                setAskTopUp(true)
              }}
            >
              공개 코드 {count}장 발급
            </Button>
          </div>
          {codes.length > 0 && (
            <ol className="space-y-1 text-table">
              {codes.map((c, i) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2">
                  <span className="w-5 text-right tabular-nums text-muted-foreground">
                    {c.state === 'issued' ? `${codes.filter((x, j) => j <= i && x.state === 'issued').length}.` : '–'}
                  </span>
                  <Code className={c.state === 'void' ? 'text-muted-foreground line-through' : undefined}>{c.code}</Code>
                  {c.state === 'void' ? (
                    <span className="text-meta text-muted-foreground">무효</span>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-status-failed" onClick={() => setToVoid(c)}>
                      무효
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          )}
          {codesQ.error != null && <ErrorState title="공개 코드를 읽지 못했다" error={codesQ.error} />}
        </div>
      ) : (
        <p className="text-meta text-muted-foreground">
          코드 방식이 none이라 공개 코드를 붙이지 않는다. 편집에서 body나 reply로 바꾸면 발급할 수 있다.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="올린 글 주소 (https://…)"
          aria-label="올린 글 주소"
          className="h-8 min-w-0 flex-1 text-table"
        />
        <Button
          size="sm"
          variant="primary"
          loading={actions.markRedeemPublished.isPending}
          disabled={!url.trim() || url.trim() === post.external_url || !!urlProblem}
          onClick={() => void markPublished()}
        >
          {post.status === 'published' ? '주소 고치기' : '게시됨으로 표시'}
        </Button>
        {urlProblem && (
          <p role="alert" className="basis-full text-meta text-status-failed">
            {urlProblem}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={askTopUp}
        onOpenChange={setAskTopUp}
        title={`공개 코드 ${more}장을 새로 발급할까?`}
        description="공개된 코드는 풀로 되돌릴 수 없다."
        confirmLabel="발급"
        onConfirm={async () => {
          try {
            await actions.topUpCodes.mutateAsync({ id: post.id, count })
            toast.success(`${more}장 발급했다`)
          } catch (e) {
            toast.error(errorText(e))
          }
          setAskTopUp(false)
        }}
      />
      <ConfirmDialog
        open={!!toVoid}
        onOpenChange={(o) => !o && setToVoid(null)}
        title={`${toVoid?.code ?? ''}을 무효 처리할까?`}
        description="다음 발급 때 한 장이 새로 채워진다."
        confirmLabel="무효"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (!toVoid) return
          try {
            await actions.voidCode.mutateAsync(toVoid.id)
            toast.success('무효 처리했다')
          } catch (e) {
            toast.error(errorText(e))
          }
          setToVoid(null)
        }}
      />
    </section>
  )
}
